import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import VanillaContextMenu from 'vanilla-context-menu';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import SetNormalHoursDrawer from 'components/drawers/location-hours-drawers/set-normal-hours-drawer/set-normal-hours-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import CustomHeaderToolbar from './custom-header-toolbar/custom-header-toolbar.component';
import locationHoursEvent from '../location-hours-event/location-hours-event.component';
import { useLocationHoursContext } from '../location-hours.context';
import { Paper, Stack, useTheme } from '@mui/material';
import '../index.css';
import { convertToSetHoursFormData } from '../location-hours.utils';

export const NormalHoursCalendar = () => {
  const { t } = useTranslation();
  const {
    normalHours: { normalEvents, normalHoursRef, addDummyEvent, eventsSet },
  } = useLocationHoursContext();

  const setNormalHoursDrawer = useUtilityDrawer();

  const theme = useTheme();

  const root = document.documentElement;
  root.style.setProperty(
    '--fc-page-bg-color',
    theme.palette.background.default,
  );

  root.style.setProperty(
    '--context-menu-item-text-color',
    theme.palette.text.primary,
  );

  root.style.setProperty(
    '--context-menu-item-hover-bg-color',
    theme.palette.action.hover,
  );

  return (
    <Paper sx={{ px: 2, pb: 2 }}>
      <Stack gap={1} data-testid="NormalHours.Calendar">
        <CustomHeaderToolbar />
        <FullCalendar
          plugins={[timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="timeGridWeek" // Set initial view to Week
          now={DateTime.local().toJSDate()}
          initialEvents={normalEvents}
          eventOrder="-title" // otherwise events can swap columns and we don't want that
          eventsSet={eventsSet}
          allDaySlot={false}
          headerToolbar={false}
          editable
          eventResizableFromStart
          ref={normalHoursRef}
          eventOverlap={(stillEvent, movingEvent) => {
            return (
              stillEvent.extendedProps.orderType !==
              movingEvent.extendedProps.orderType
            );
          }}
          dayHeaderFormat={{ weekday: 'short' }}
          dateClick={dateSelect => {
            const start = DateTime.fromJSDate(dateSelect.date);
            const end = start.plus({ minutes: 30 });

            const dummyEvent = addDummyEvent(start, end);

            setNormalHoursDrawer.toggleWithData(
              convertToSetHoursFormData({
                title: dummyEvent.title,
                start,
                end,
              }),
            );
          }}
          eventClick={({ event }) => {
            setNormalHoursDrawer.toggleWithData(
              convertToSetHoursFormData({
                id: event.id,
                title: event.title,
                start: DateTime.fromJSDate(event.start),
                end: DateTime.fromJSDate(event.end),
              }),
            );
          }}
          scrollTime="00:00:00"
          eventDidMount={({ event, el }) => {
            new VanillaContextMenu({
              scope: el,
              normalizePosition: false,
              transitionDuration: 0,
              customClass: 'context-menu-custom',
              customThemeClass: 'context-menu-item-custom',
              menuItems: [
                {
                  label: t(
                    'LocationHours.normalHours.calendar.event.menuItems.edit',
                  ),
                  callback: () => {
                    setNormalHoursDrawer.toggleWithData(
                      convertToSetHoursFormData({
                        id: event.id,
                        title: event.title,
                        start: DateTime.fromJSDate(event.start),
                        end: DateTime.fromJSDate(event.end),
                      }),
                    );
                  },
                },
                'hr',
                {
                  label: t(
                    'LocationHours.normalHours.calendar.event.menuItems.delete',
                  ),
                  callback: () => {
                    event.remove();
                  },
                },
              ],
            });
          }}
          eventContent={locationHoursEvent}
          height="auto"
          listDaySideFormat={false}
        />
        <SetNormalHoursDrawer {...setNormalHoursDrawer} />
      </Stack>
    </Paper>
  );
};
export default NormalHoursCalendar;
